import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { ChevronLeft, ChevronRight, MapPin } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mini carousel for each project card
const CardCarousel = ({ images, area }) => {
  const [idx, setIdx] = useState(0);
  if (!images || images.length === 0) return <div className="w-full h-full bg-gray-200" />;
  return (
    <div className="relative w-full h-full group/carousel">
      <img src={images[idx]} alt="" className="w-full h-full object-cover" />
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
        <div className="flex items-center gap-1 text-white font-bold" style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: '20pt' }}><MapPin className="w-5 h-5" />{area}</div>
      </div>
      {images.length > 1 && (
        <>
          <button onClick={e => { e.stopPropagation(); setIdx(i => (i - 1 + images.length) % images.length); }} className="absolute left-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={e => { e.stopPropagation(); setIdx(i => (i + 1) % images.length); }} className="absolute right-1 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/40 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity"><ChevronRight className="w-4 h-4" /></button>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-1">
            {images.map((_, i) => <div key={i} className={`w-1.5 h-1.5 rounded-full ${i === idx ? 'bg-white' : 'bg-white/40'}`} />)}
          </div>
        </>
      )}
    </div>
  );
};

const PortfolioSection = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [videoProjects, setVideoProjects] = useState([]);
  const [videoIdx, setVideoIdx] = useState(0);

  useEffect(() => {
    const load = async () => {
      try {
        const [featuredRes, videoRes] = await Promise.all([
          axios.get(`${API}/admin/projects/featured`),
          axios.get(`${API}/admin/projects/with-video`),
        ]);
        setProjects(featuredRes.data.projects || []);
        setVideoProjects(videoRes.data.projects || []);
      } catch {}
    };
    load();
  }, []);

  const handleVideoEnded = useCallback(() => {
    setVideoIdx(prev => (prev + 1) % (videoProjects.length || 1));
  }, [videoProjects.length]);

  const currentVideo = videoProjects[videoIdx] || null;

  return (
    <section id="portfolio" className="py-20 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{t('portfolio.title')}</h2>
          <p className="text-xl text-gray-600">{t('portfolio.subtitle')}</p>
        </div>

        {/* 6 Featured Project Cards */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {projects.slice(0, 6).map((p) => (
            <div key={p.id} className="group overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer" style={{ width: '400px' }} onClick={() => navigate('/portfolio')}>
              <div style={{ height: '230px' }}>
                <CardCarousel images={p.images} area={p.area || p.address} />
              </div>
              <div className="bg-white p-3">
              </div>
            </div>
          ))}
        </div>

        {/* Featured Project — video auto-play, advance on ended */}
        {currentVideo ? (
          <Card className="max-w-4xl mx-auto overflow-hidden shadow-2xl border-0">
            <div className="grid md:grid-cols-2">
              <div className="h-64 md:h-auto relative bg-black min-h-[280px] overflow-hidden">
                {currentVideo.video_link ? (
                  <a href={currentVideo.video_link} target="_blank" rel="noopener noreferrer" className="block w-full h-full relative cursor-pointer">
                    <img src={currentVideo.images?.[0] || ''} alt={currentVideo.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-black/20 flex items-center justify-center hover:bg-black/40 transition-colors">
                      <svg className="w-16 h-16 drop-shadow-lg" viewBox="0 0 64 64" fill="none"><polygon points="24,14 24,50 50,32" fill="rgba(255,255,255,0.85)" /></svg>
                    </div>
                  </a>
                ) : (
                  <img src={currentVideo.images?.[0] || ''} alt={currentVideo.title} className="w-full h-full object-cover" />
                )}
              </div>
              <CardContent className="p-8 flex flex-col justify-center bg-gradient-to-br from-red-50 to-white">
                <div className="inline-block px-4 py-1 bg-red-600 text-white text-sm font-semibold rounded-full mb-4 w-fit">{t('portfolio.featured')}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{currentVideo.area || currentVideo.title}</h3>
                <div className="flex items-center gap-1 text-gray-500 text-sm mb-3"><MapPin className="w-3.5 h-3.5" />{currentVideo.area || ''}</div>
                <p className="text-gray-600 mb-4 leading-relaxed text-sm">{isZh ? currentVideo.description_zh : currentVideo.description_en}</p>
                {/* Video Button */}
                {currentVideo.video_link && (
                  <a href={currentVideo.video_link} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold px-5 py-2 rounded-lg mb-4 w-fit transition-colors">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>Video
                  </a>
                )}
                {/* Video indicators */}
                {videoProjects.length > 1 && (
                  <div className="flex space-x-2 mb-4">
                    {videoProjects.map((_, i) => (
                      <button key={i} onClick={() => setVideoIdx(i)} className={`h-2 rounded-full transition-all duration-300 ${i === videoIdx ? 'w-8 bg-red-600' : 'w-2 bg-gray-300 hover:bg-gray-400'}`} />
                    ))}
                  </div>
                )}
                <button className="text-red-600 font-semibold hover:text-red-700 transition-colors flex items-center group" onClick={() => navigate('/portfolio')}>
                  {t('portfolio.viewAll')}<span className="ml-2 group-hover:ml-4 transition-all duration-300">&rarr;</span>
                </button>
              </CardContent>
            </div>
          </Card>
        ) : (
          <div className="text-center">
            <button className="text-red-600 font-semibold hover:text-red-700 transition-colors flex items-center group mx-auto text-lg" onClick={() => navigate('/portfolio')}>
              {t('portfolio.viewAll')}<span className="ml-2 group-hover:ml-4 transition-all duration-300">&rarr;</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PortfolioSection;
